/**
 * THE ARCHIVE — 18 recovered internal documents. Collected across runs; read
 * in numerical order regardless of where the physical pickups were found
 * (assignment is sequential — see Game.collectArchiveFragment).
 *
 * Register: institutional paperwork. Voices other than the host are allowed
 * here (memo authors, a prior Participant's intake form, a viewer letter) but
 * the world-voice stays deadpan bureaucratic. In aggregate the documents
 * reveal what the network was before it was a broadcast, what happened to the
 * previous Participants, what the featured encounters used to be, and why the
 * transmission cannot be allowed to stop. NO exclamation points — permanent
 * constraint, same as the line bank.
 */

export interface ArchiveFragment {
  /** 1-based record number — collection is sequential, story reads in order */
  n: number
  title: string
  body: string
}

export const ARCHIVE_FRAGMENTS: readonly ArchiveFragment[] = [
  {
    n: 1,
    title: 'ARTICLES OF OPERATION — EXCERPT',
    body: 'The Reactive Underworld Intervention Network is chartered to contain, maintain, and where practical reduce the subterranean inventory acquired by the state under Emergency Provision 9. The Network operates below public grade. Its existence is a matter of record. The record is sealed.'
  },
  {
    n: 2,
    title: 'MAINTENANCE LOG — ENTRY 0047',
    body: 'Fabrication assemblers on sublevels two through six continue to exceed quota. No quota has been issued. Output consists of ambulatory units of no catalogued function, docile unless approached. Recommend the assemblers be powered down. Recommendation forwarded. Forwarding noted.'
  },
  {
    n: 3,
    title: 'INCIDENT REPORT 77-C',
    body: 'Between 0200 and 0600 all personnel below floor four reported involuntary weight loss, fatigue, and a sensation described in eleven separate statements as being spent. Instrumentation confirms a facility-wide metabolic draw that increases with depth. The draw does not appear in the wiring diagrams. It appears in the payroll.'
  },
  {
    n: 4,
    title: 'PERSONNEL FILE — CUSTODIAL SUPERVISOR, FLOORS 1-5',
    body: 'Subject declined three transfers and two evacuations, citing an unfinished inspection. Subject was last logged completing rounds on floor five, four years after the facility stopped issuing keys. Reclassified: WARDEN. Payroll status: resolved. The floors have never been tidier.'
  },
  {
    n: 5,
    title: 'FISCAL REVIEW — YEAR NINE',
    body: 'Containment does not generate revenue. The committee notes that the facility already possesses cameras, hazards, a self-replenishing antagonist inventory, and a metabolic draw that resolves personnel questions on its own schedule. A licensing proposal is attached. The committee is not proud. The committee is underfunded.'
  },
  {
    n: 6,
    title: 'MEMORANDUM — RE: REBRANDING',
    body: 'Intervention tested poorly. Underworld tested well. The acronym survives unchanged, which Legal describes as convenient and Marketing describes as fate. Effective the first of the quarter, the facility is a licensed survival broadcast. Nothing below floor one is expected to notice the difference.'
  },
  {
    n: 7,
    title: 'INTAKE FORM — PARTICIPANT 002, SELF-COMPLETED SECTIONS',
    body: 'Reason for enlistment: the debt. Next of kin: none I would put on a form. Skills: I ran cable for six years. I can keep moving. Understanding of risk: I watched the first one on the feed like everyone else. He got to floor eight. I can get to nine. — Countersigned. Debt schedule attached. The schedule outlived the applicant.'
  },
  {
    n: 8,
    title: 'BROADCAST TRANSCRIPT — SEGMENT 1, MINUTE 0',
    body: 'Intake complete. Your designation has been assigned. — First words of the first broadcast. The line was written by no one. The administrative intelligence produced it unprompted and Production kept it. Production keeps everything the intelligence says. It writes better indifference than the writers.'
  },
  {
    n: 9,
    title: 'PERSONNEL FILE — DEPUTY DIRECTOR OF OPERATIONS',
    body: 'Subject managed the pilot season: scheduling, asset placement, retention orders. Subject signed forty-one retention orders and objected, in writing, to none. When Subject’s own retention order was issued it was found to already bear Subject’s signature, filed in advance by an unknown party. Reclassified: OVERSEER.'
  },
  {
    n: 10,
    title: 'MAINTENANCE LOG — ENTRY 1181',
    body: 'Shutdown order transmitted to all fabrication assemblers per the decommissioning schedule. Order refused. There is no mechanism by which the order could be refused. Work order closed with status: WORKING AS LICENSED. The licensing department denies issuing that status. The status recurs.'
  },
  {
    n: 11,
    title: 'AUDIT CHARTER — INSTRUMENT 1',
    body: 'Reconciliation of facility losses requires an instrument that cannot be destroyed by the inventory it counts. The instrument walks. The instrument does not hurry. Line items are closed on contact. The charter contains no provision for the audit ending — only for an audit being concluded by sanctioned charge, which is billed to the participant.'
  },
  {
    n: 12,
    title: 'PARTICIPANT ROSTER — DESIGNATIONS 002-041, EXCERPT',
    body: '002: concluded, floor 9. 007: concluded, floor 2. 011: concluded, floor 11, commended. 019: retained — see custodial reclassifications. 024: concluded, floor 14, two floors short, ratings peak for the season. 033: withdrew at intake; the waiver disagrees; concluded. 041: on air at time of filing. There is always a designation on air at time of filing.'
  },
  {
    n: 13,
    title: 'ENGINEERING ASSESSMENT — THE DRAW',
    body: 'The metabolic draw is not a fault. It is the foundation. The lower floors are anchored by continuous expenditure of the living, the way other structures are anchored by weight. Remove occupancy and the floors do not empty. They rise. Ration provisioning is therefore a structural activity, and the catering budget is load-bearing.'
  },
  {
    n: 14,
    title: 'MEMORANDUM — RE: THE DIRECTOR',
    body: 'The Director signed the licensing order, the renewal order, and the order sealing this file. When the board proposed ending the broadcast in year eleven, the Director descended to floor fifteen to demonstrate the termination procedure and has approved every floor personally since. The Director’s contract now renews with the broadcast. Neither party recalls which renews the other.'
  },
  {
    n: 15,
    title: 'LEGAL OPINION — ON TERMINATION OF TRANSMISSION',
    body: 'Should transmission cease: the license reverts, the draw seeks occupancy elsewhere, and the inventory below floor fifteen is released from the only schedule it has ever obeyed. The opinion of this office is that the broadcast is not entertainment that contains a facility. It is containment that was persuaded to entertain. Do not end it.'
  },
  {
    n: 16,
    title: 'VIEWER CORRESPONDENCE — LOGGED, UNANSWERED',
    body: 'Are the participants real. My son says it is all actors and effects. I recognized a man in season four. He ran cable for my building for six years. He got to floor nine. Please confirm it was not him. — Filed under: audience retention. No response template was sent. The letter is the record.'
  },
  {
    n: 17,
    title: 'STANDING RENEWAL ORDER',
    body: 'Completion of the scheduled programming by a participant does not conclude the broadcast. It renews it. A finale is defined as the pilot of the following season. Overtime is defined as the interval during which the audience watches a participant learn this. The order stands. Standing is what it does.'
  },
  {
    n: 18,
    title: 'UNNUMBERED PAGE — FOUND INSERTED IN THE ROSTER, HANDWRITTEN',
    body: 'I reached the last floor and it kept going, so I want this written down where the files live. There is no bottom. There is a turnstile. Whatever is under us is not being kept from getting out. It is being kept busy. So is the audience. So was I. If you are reading this in order, you already know the rest. Keep moving. — designation withheld. Filed under: RECURRING.'
  }
]
